import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

import { useShallow } from 'zustand/react/shallow';

import { useRoomStore } from '@src/stores';

import { EMeetingRoutes, EWebinarRoutes } from '@src/constants';

import { EMeetingType } from '@src/types';

type TTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: TTheme;
  toggleTheme: () => void;
  setTheme: (theme: TTheme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const LIGHT_THEME_PATHS = [
  EWebinarRoutes.CLOSED,
  EWebinarRoutes.FULL,
  EWebinarRoutes.CREATE,
  EWebinarRoutes.REGISTRATION_FORM,
  EWebinarRoutes.UPDATE,
  EWebinarRoutes.ADMIN_WEBINAR,
  EMeetingRoutes.HOME,
];

export const ThemeProvider: React.FC<{
  children: React.ReactNode;
  ignorePathCheck?: boolean;
  targetDocument?: Document;
}> = ({ children, ignorePathCheck = false, targetDocument }) => {
  const location = useLocation();
  const resolvedTargetDocument = targetDocument ?? document;

  const [theme, setThemeState] = useState<TTheme>(() => {
    return (localStorage.getItem('theme') as TTheme) ?? 'dark';
  });

  const { isJoinedRoom, meetingType } = useRoomStore(
    useShallow((state) => ({
      isJoinedRoom: state.isJoinedRoom,
      meetingType: state.meetingType,
    }))
  );

  const isLightThemeRoute = useMemo(
    () =>
      !ignorePathCheck &&
      (LIGHT_THEME_PATHS.some((path) => matchPath({ path, end: true }, location.pathname)) ||
        // This will be removed when implement the dark theme for join screens on webinar
        (!isJoinedRoom &&
          meetingType === EMeetingType.WEBINAR &&
          !!matchPath({ path: EWebinarRoutes.JOIN, end: true }, location.pathname))),
    [ignorePathCheck, isJoinedRoom, location.pathname, meetingType]
  );

  useEffect(() => {
    resolvedTargetDocument.documentElement.setAttribute('data-theme', isLightThemeRoute ? 'light' : theme);
  }, [resolvedTargetDocument, theme, isLightThemeRoute]);

  useEffect(() => {
    if (!isLightThemeRoute) {
      localStorage.setItem('theme', theme);
    }
  }, [theme, isLightThemeRoute]);

  const toggleTheme = () => {
    if (!isLightThemeRoute) {
      setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
    }
  };

  const setTheme = (newTheme: TTheme) => {
    if (!isLightThemeRoute) {
      setThemeState(newTheme);
    }
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
